import React, { useCallback, useMemo, useState } from "react"

import { useAgentsById } from "@/agents/catalog"
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  RadioGroup,
  RadioGroupItem,
  SearchInput,
  toast,
} from "@coval/ui"
import { Bot, Copy, Star, Trash2, X } from "lucide-react"

import { useTestSetEditorContext } from "../contexts/TestSetEditorContext"
import { EVENTS, useEventDispatcher } from "../event-bus"
import { AddTestCaseButton } from "./test-case-list/AddTestCaseButton"
import { TestCaseFilterControls } from "./TestCaseFilterControls"

export const TestSetAgentLink: React.FC = () => {
  const {
    selectedAgentIds,
    activeAgentId,
    setSelectedAgentIds,
    setActiveAgentId,
    selectedTestCaseIds,
    selectableTestCases,
  } = useTestSetEditorContext()
  const dispatch = useEventDispatcher()
  const [searchQuery, setSearchQuery] = useState("")
  const [isOpen, setIsOpen] = useState(false)

  const { agentsById, isLoading: isLoadingAgents } = useAgentsById()

  // Convert agentsById to array, filtering out inactive agents
  const agents = useMemo(() => {
    return Object.values(agentsById).filter(
      (agent) => (agent as { active?: boolean }).active !== false,
    )
  }, [agentsById])

  const selectedSet = useMemo(
    () => new Set(selectedAgentIds),
    [selectedAgentIds],
  )

  const filteredAgents = useMemo(() => {
    if (!searchQuery) {
      return agents
    }
    return agents.filter((agent) =>
      agent.display_name.toLowerCase().includes(searchQuery.toLowerCase()),
    )
  }, [agents, searchQuery])

  const toggleAgent = useCallback(
    (agentId: string) => {
      const newSelected = new Set(selectedSet)
      if (newSelected.has(agentId)) {
        newSelected.delete(agentId)
        // If removing the active agent, set to first remaining or clear
        if (activeAgentId === agentId) {
          const remaining = Array.from(newSelected)
          setActiveAgentId(remaining.length > 0 ? remaining[0] : "")
        }
      } else {
        newSelected.add(agentId)
        // If no active agent is set, set this one as active
        if (!activeAgentId) {
          setActiveAgentId(agentId)
        }
      }
      const newSelectedArray = Array.from(newSelected)
      setSelectedAgentIds(newSelectedArray)

      // Ensure active agent is in selected agents after update
      if (activeAgentId && !newSelectedArray.includes(activeAgentId)) {
        setActiveAgentId(newSelectedArray.length > 0 ? newSelectedArray[0] : "")
      }
    },
    [selectedSet, activeAgentId, setSelectedAgentIds, setActiveAgentId],
  )

  const handleActiveAgentChange = useCallback(
    (agentId: string) => {
      setActiveAgentId(agentId)
    },
    [setActiveAgentId],
  )

  const getButtonText = () => {
    if (selectedAgentIds.length === 0) {
      return "Link agents"
    }
    if (selectedAgentIds.length === 1) {
      const agent = agentsById[selectedAgentIds[0]]
      return agent?.display_name || "1 agent linked"
    }
    return `${selectedAgentIds.length} agents linked`
  }

  const selectionCount = selectedTestCaseIds.size

  const handleDuplicate = useCallback(() => {
    const copies = selectableTestCases
      .filter((tc) => selectedTestCaseIds.has(tc.id))
      .map((tc, i) => ({ ...tc, id: `new-${Date.now()}-${i}` }))
    dispatch(EVENTS.BULK_ADD, { testCases: copies })
    dispatch(EVENTS.TEST_CASE_CLEAR_SELECTION, {})
    toast.success({
      title: "Success",
      description: `Duplicated ${copies.length} row${copies.length !== 1 ? "s" : ""}`,
    })
  }, [selectableTestCases, selectedTestCaseIds, dispatch])

  const handleDelete = useCallback(() => {
    const ids = Array.from(selectedTestCaseIds)
    dispatch(EVENTS.BULK_DELETE, { testCaseIds: ids })
    dispatch(EVENTS.TEST_CASE_CLEAR_SELECTION, {})
    toast.success({
      title: "Success",
      description: `Successfully deleted ${selectionCount} row${selectionCount !== 1 ? "s" : ""}`,
    })
  }, [selectedTestCaseIds, selectionCount, dispatch])

  if (selectionCount > 0) {
    return (
      <div className="shrink-0 w-full border-b border-coval-border px-6 py-3">
        <div className="flex flex-wrap items-center gap-4">
          <AddTestCaseButton />

          <Button
            variant="secondary"
            onClick={() => dispatch(EVENTS.TEST_CASE_CLEAR_SELECTION, {})}
            className="font-medium"
          >
            {selectionCount} selected
            <X className="w-3.5 h-3.5" />
          </Button>
          <Button variant="secondary" onClick={handleDuplicate}>
            <Copy className="w-4 h-4" />
            Duplicate
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            <Trash2 className="w-4 h-4" />
            Delete
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="shrink-0 w-full border-b border-coval-border px-6 py-3">
      <div className="flex flex-wrap items-center gap-4">
        <AddTestCaseButton />

        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              disabled={isLoadingAgents}
              className="justify-start"
            >
              <Bot className="w-4 h-4" />
              {getButtonText()}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="min-w-64 max-w-80 max-h-[400px] overflow-y-auto"
          >
            <div className="p-2 sticky top-0 z-20 bg-surface-0">
              <SearchInput
                aria-label="Search agents"
                placeholder="Search agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClear={() => setSearchQuery("")}
              />
            </div>
            {filteredAgents.length === 0 ? (
              <DropdownMenuItem disabled>
                <span className="text-coval-text-muted">
                  {searchQuery ? "No agents found" : "No agents available"}
                </span>
              </DropdownMenuItem>
            ) : (
              filteredAgents.map((agent) => {
                const isSelected = selectedSet.has(agent.id)
                const isActive = activeAgentId === agent.id

                return (
                  <DropdownMenuItem
                    key={agent.id}
                    onSelect={(e) => {
                      e.preventDefault()
                      toggleAgent(agent.id)
                    }}
                    className="p-0"
                  >
                    <div
                      className="flex items-center gap-2 w-full p-2"
                      style={{ width: "250px" }}
                    >
                      <Checkbox
                        checked={isSelected}
                        tabIndex={-1}
                        aria-hidden
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-medium truncate">
                            {agent.display_name}
                          </div>
                          {isActive && isSelected && (
                            <Star className="w-3 h-3 text-coval-contrast fill-coval-contrast" />
                          )}
                        </div>
                      </div>
                    </div>
                  </DropdownMenuItem>
                )
              })
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <TestCaseFilterControls />

        {selectedAgentIds.length > 0 && (
          <>
            <span className="text-sm text-coval-text-muted whitespace-nowrap shrink-0">
              View as:
            </span>
            <div
              className="flex-[3] min-w-0 overflow-x-auto"
              style={{ scrollbarWidth: "none" }}
            >
              <RadioGroup
                value={activeAgentId || ""}
                onValueChange={handleActiveAgentChange}
                className="flex flex-row gap-3 flex-nowrap"
              >
                {selectedAgentIds.map((agentId) => {
                  const agent = agentsById[agentId]
                  if (!agent) {
                    return null
                  }

                  return (
                    <label
                      key={agentId}
                      className="flex items-center gap-2 m-0 whitespace-nowrap cursor-pointer"
                    >
                      <RadioGroupItem value={agentId} />
                      <span className="text-sm">{agent.display_name}</span>
                    </label>
                  )
                })}
              </RadioGroup>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
